import { getFixedStoreAlias } from "@/helpers/urlHelper";

const DB_VERSION = "2";
// Scope persisted cart data by browser host and store alias.
const getDatabaseName = () => {
  const paths = window.location.pathname.split("/").filter((p) => p);
  // In single-store mode routes omit /:storeAlias (e.g. /checkout vs /store/checkout).
  // Always use the fixed alias so cart persistence survives route changes.
  const storeAlias = getFixedStoreAlias() || paths[0] || "default";
  const host = window.location.host || "default";
  return `biyo-order:${host}:${storeAlias}`;
};
let DB;
let currentDatabaseName;
const stores = [
  { name: "orderItems", key: "local_id" },
  { name: "tableOrderItems", key: "local_id" }
];

export default {
  async initialize() {
    return new Promise((resolve, reject) => {
      const databaseName = getDatabaseName();
      if (DB && currentDatabaseName === databaseName) return resolve(DB);
      if (DB) {
        DB.close();
        DB = null;
        currentDatabaseName = null;
      }
      const request = window.indexedDB.open(databaseName, DB_VERSION);
      request.onerror = (e) => reject(e);
      request.onsuccess = (e) => {
        DB = e.target.result;
        currentDatabaseName = databaseName;
        DB.onversionchange = () => {
          DB.close();
          DB = null;
          currentDatabaseName = null;
        };
        resolve(DB);
      };
      request.onupgradeneeded = (e) => {
        const db = e.target.result;
        stores.forEach((store) => {
          try {
            if (!db.objectStoreNames.contains(store.name)) {
              db.createObjectStore(store.name, { keyPath: store.key });
            }
          } catch (err) {
            console.log(err);
          }
        });
      };
    });
  },

  async saveToDb(key, data) {
    const db = await this.initialize();
    return new Promise((resolve) => {
      try {
        const trans = db.transaction([key], "readwrite");
        trans.oncomplete = () => resolve();
        const store = trans.objectStore(key);
        if (Array.isArray(data)) {
          data.forEach((updateOrder) => store.put(updateOrder));
        } else {
          store.put(data);
        }
      } catch (err) {
        //pass
      }
    });
  },

  async getDbData(key) {
    const db = await this.initialize();
    return new Promise((resolve) => {
      try {
        const trans = db.transaction([key], "readonly");
        const store = trans.objectStore(key);
        const dbData = [];

        store.openCursor().onsuccess = (e) => {
          const cursor = e.target.result;
          if (cursor) {
            dbData.push(cursor.value);
            cursor.continue();
          } else {
            // Cursor finished, resolve with data
            resolve(dbData);
          }
        };

        trans.onerror = () => {
          console.error("Error reading from IndexDB:", key);
          resolve([]); // Return empty array on error
        };
      } catch (err) {
        console.error("Error in getDbData:", err, key);
        resolve([]); // Always resolve with empty array on error
      }
    });
  },

  async clearAllData(store = null) {
    /**
     * @param {Array ,String} [store] - array of index db store names of name of index db store
     *
     * @example
     *  clearAllData()
     *  clearAllData(["orders","categories"])
     *  clearAllData("orders")
     */
    const db = await this.initialize();
    return new Promise((resolve, reject) => {
      try {
        if (!store) {
          stores.forEach((store) => {
            const trans = db.transaction(store.name, "readwrite");
            const requestedStore = trans.objectStore(store.name);
            requestedStore.clear();
            trans.oncomplete = () => {
              resolve();
            };
          });
        } else if (Array.isArray(store)) {
          store.forEach((store) => {
            const trans = db.transaction(store, "readwrite");
            const requestedStore = trans.objectStore(store);
            requestedStore.clear();
            trans.oncomplete = () => {
              resolve();
            };
          });
        } else if (typeof store === "string") {
          const trans = db.transaction([store], "readwrite");
          const requestedStore = trans.objectStore(store);
          requestedStore.clear();
          trans.oncomplete = () => {
            resolve();
          };
        }
      } catch (err) {
        reject();
      }
    });
  },

  async deleteSingleInstance(storeName, instanceKey) {
    const db = await this.initialize();
    return new Promise((resolve, reject) => {
      try {
        const trans = db.transaction(storeName, "readwrite");
        const requestedStore = trans.objectStore(storeName);
        requestedStore.delete(instanceKey);
        trans.oncomplete = () => {
          resolve();
        };
      } catch (err) {
        reject(err);
      }
    });
  }
};
